import { useEffect, useId, useRef } from "react";
import { cn } from "../../lib/cn";

export function Popover({ open, onOpenChange, children, className }) {
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const onDocumentClick = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      if (event.target instanceof Element && event.target.closest("[data-picker-dropdown]")) {
        return;
      }

      const active = document.activeElement;
      if (
        active instanceof HTMLInputElement &&
        (active.type === "date" || active.type === "time") &&
        rootRef.current?.contains(active)
      ) {
        return;
      }

      onOpenChange?.(false);
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") onOpenChange?.(false);
    };

    const timer = window.setTimeout(() => {
      document.addEventListener("click", onDocumentClick);
    }, 0);

    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("click", onDocumentClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onOpenChange]);

  return (
    <div ref={rootRef} className={cn("relative inline-block max-w-full", className)}>
      {children}
    </div>
  );
}

export function PopoverTrigger({ open, className, children, ...props }) {
  return (
    <button
      type="button"
      aria-expanded={open}
      className={cn(
        "inline-flex max-w-full items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-left text-sm",
        "hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        open && "ring-2 ring-ring",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function PopoverPanel({
  id,
  title,
  description,
  className,
  children,
  footer,
}) {
  return (
    <div
      id={id}
      onMouseDown={(e) => e.stopPropagation()}
      className={cn(
        "absolute left-0 top-[calc(100%+8px)] z-30 w-[min(340px,calc(100vw-2rem))] rounded-xl border border-border bg-card shadow-lg",
        className,
      )}
    >
      {(title || description) && (
        <div className="border-b border-border px-4 py-3">
          {title && (
            <p className="text-sm font-medium text-foreground">{title}</p>
          )}
          {description && (
            <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
          )}
        </div>
      )}
      <div className="p-4">{children}</div>
      {footer && (
        <div className="border-t border-border px-4 py-3 bg-muted/20">{footer}</div>
      )}
    </div>
  );
}

export function usePopoverId() {
  return useId();
}
